import { useEffect } from "react";
import { filterTaxRegime, filterUsoCFDI } from "../constants/filters";
import type { ClienteShape } from "../types/invoice";

type Notify = (
  text: string,
  severity?: "warning" | "info" | "error" | "success"
) => void;

export function useClientValidations(opts: {
  cliente: ClienteShape | null;
  regimenFiscal: string;
  usoCFDIValue: string;
  notify?: Notify;
}) {
  const { cliente, regimenFiscal, usoCFDIValue, notify } = opts;
  const warn = (msg: string) => notify?.(msg, "warning");

  function validarRegimenVsRFC(regimen: string, rfcVal: string) {
    const cfg = filterTaxRegime.find((t) => t.value === Number(regimen));
    if (!cfg || !rfcVal) return;

    const len = rfcVal.trim().length;
    const ok =
      (cfg.person_type === 12 && len === 12) || // Moral
      (cfg.person_type === 13 && len === 13) || // Física
      (cfg.person_type === 1 && (len === 12 || len === 13)); // Aplica a Moral y Física

    if (!ok) {
      warn(
        `El régimen fiscal "${cfg.label}" no corresponde al tipo de persona "${
          len === 12 ? "Moral" : "Física"
        }" del RFC ${rfcVal}.`
      );
    }
  }

  function validarCfdiVsRegimen(regimen: string, uso: string) {
    if (!regimen || !uso) return;
    const reg = Number(regimen);
    const cfdi = filterUsoCFDI.find((c) => c.value === uso);
    if (!cfdi) return;

    if (cfdi.stf?.includes(reg)) return;

    const aceptados = filterUsoCFDI
      .filter((c) => c.stf?.includes(reg))
      .map((c) => c.value);

    warn(
      `El uso de CFDI ${uso} no aplica al régimen fiscal ${regimen}. Sugeridos: ${aceptados.join(
        ", "
      )}.`
    );
  }

  useEffect(() => {
    const rfcActual = cliente?.rfc?.trim() || "";
    const regimenActual = (
      regimenFiscal ||
      cliente?.tax_regime ||
      ""
    ).toString();
    const usoActual = (usoCFDIValue || cliente?.cfdi_use || "").toString();

    if (!rfcActual || !regimenActual || !usoActual) return;

    validarRegimenVsRFC(regimenActual, rfcActual);
    validarCfdiVsRegimen(regimenActual, usoActual);
  }, [
    cliente?.id,
    cliente?.rfc,
    cliente?.tax_regime,
    cliente?.cfdi_use,
    regimenFiscal,
    usoCFDIValue,
  ]);
}
